import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { listProjectAssetsApiProjectsProjectIdAssetsGetQueryKey } from "../generated/api/@tanstack/react-query.gen";
import {
  attachProjectAssetApiProjectsProjectIdAssetsPost,
  createAssetApiAssetsPost,
  createImageAssetApiAssetsImagePost,
} from "../generated/api/sdk.gen";
import { useAssets } from "../hooks/useAssets";
import {
  useColorAssetForm,
  useColorPickerModalState,
} from "../hooks/useColorPickerModal";
import { TAB_LABEL } from "../lib/asset-modal-tabs";
import { buildAssetCreateBody } from "../lib/color-picker";
import { invalidateAssets } from "../lib/queries";
import { AssetBrowserTab } from "./AssetBrowserTab";
import { AssetModalChrome } from "./AssetModalChrome";
import { ColorAssetFormFields, ColorPickerFields } from "./ColorPickerFields";
import {
  ImageAssetUploadFields,
  useImageFileSelection,
} from "./ImageAssetUploadFields";

interface AddProjectAssetModalProps {
  isOpen: boolean;
  projectId: string;
  onClose: () => void;
}

type AddAssetTab = "assets" | "image" | "color";

const TABS = [
  { id: "assets" as AddAssetTab, label: TAB_LABEL.assets },
  { id: "image" as AddAssetTab, label: TAB_LABEL.image },
  { id: "color" as AddAssetTab, label: TAB_LABEL.color },
];

function NewAssetForm({
  pickerState,
  assetForm,
}: {
  pickerState: ReturnType<typeof useColorPickerModalState>;
  assetForm: ReturnType<typeof useColorAssetForm>;
}) {
  return (
    <>
      <ColorPickerFields pickerState={pickerState} />
      <div className="mt-6">
        <ColorAssetFormFields assetForm={assetForm} />
      </div>
    </>
  );
}

/** Attach work shared by both tabs: invalidate the project's asset list (and
 *  the global asset list, since "New" also adds to the library) then close. */
function useAttachInvalidation(projectId: string, onClose: () => void) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: listProjectAssetsApiProjectsProjectIdAssetsGetQueryKey({
        path: { project_id: projectId },
      }),
    });
    invalidateAssets(queryClient);
    onClose();
  };
}

export function AddProjectAssetModal({
  isOpen,
  projectId,
  onClose,
}: AddProjectAssetModalProps) {
  // Matches the open-reset below: initializing to anything else paints the
  // wrong tab for one frame before the effect corrects it.
  const [activeTab, setActiveTab] = useState<AddAssetTab>("assets");
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);

  const pickerState = useColorPickerModalState({
    isOpen,
    initialHex: "#ffffff",
  });
  const assetForm = useColorAssetForm(isOpen, true);
  const { file, setFile, previewUrl } = useImageFileSelection(isOpen);
  const [imageName, setImageName] = useState("");
  const assetsQuery = useAssets(isOpen && activeTab === "assets");
  const onAttached = useAttachInvalidation(projectId, onClose);

  useEffect(() => {
    if (isOpen) {
      setActiveTab("assets");
      setSelectedAssetId(null);
      setImageName("");
    }
  }, [isOpen]);

  const attach = (assetId: string) =>
    attachProjectAssetApiProjectsProjectIdAssetsPost({
      path: { project_id: projectId },
      body: { asset_id: assetId },
    });

  const createAndAttach = useMutation({
    mutationFn: async () => {
      const { data } = await createAssetApiAssetsPost({
        body: buildAssetCreateBody(
          assetForm.name.trim(),
          pickerState.selectedHex,
        ),
      });
      if (!data) throw new Error("Failed to create asset");
      await attach(data.id);
    },
    onSuccess: onAttached,
  });

  const uploadAndAttach = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("No file selected");
      const { data } = await createImageAssetApiAssetsImagePost({
        body: { file, name: imageName.trim() },
      });
      if (!data) throw new Error("Failed to create asset");
      await attach(data.id);
    },
    onSuccess: onAttached,
  });

  const attachExisting = useMutation({
    mutationFn: async (assetId: string) => {
      await attach(assetId);
    },
    onSuccess: onAttached,
  });

  if (!isOpen) return null;

  const isPending =
    createAndAttach.isPending ||
    uploadAndAttach.isPending ||
    attachExisting.isPending;
  const isError =
    createAndAttach.isError ||
    uploadAndAttach.isError ||
    attachExisting.isError;
  const okDisabled =
    isPending ||
    (activeTab === "color"
      ? assetForm.name.trim().length === 0
      : activeTab === "image"
        ? !file || imageName.trim().length === 0
        : selectedAssetId === null);

  const handleOk = () => {
    if (activeTab === "color") {
      createAndAttach.mutate();
    } else if (activeTab === "image") {
      uploadAndAttach.mutate();
    } else if (selectedAssetId) {
      attachExisting.mutate(selectedAssetId);
    }
  };

  return (
    <AssetModalChrome
      ariaContext="add asset"
      title="Add Asset"
      subtitle="Create a new asset or add an existing one to this project."
      tabs={TABS}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      primary={{
        label: isPending ? "Adding\u2026" : "Add",
        disabled: okDisabled,
        onClick: handleOk,
      }}
      onClose={onClose}
    >
      {activeTab === "color" ? (
        <NewAssetForm pickerState={pickerState} assetForm={assetForm} />
      ) : activeTab === "image" ? (
        <ImageAssetUploadFields
          file={file}
          previewUrl={previewUrl}
          onSelectFile={setFile}
          name={imageName}
          onNameChange={setImageName}
        />
      ) : (
        <AssetBrowserTab
          query={assetsQuery}
          enabled={isOpen && activeTab === "assets"}
          selectedAssetId={selectedAssetId}
          onSelect={(asset) => setSelectedAssetId(asset.id)}
          emptyMessage="No saved assets yet."
        />
      )}

      {isError && (
        <p className="mt-4 text-sm text-red-400" role="alert">
          Couldn&apos;t add this asset. Please try again.
        </p>
      )}
    </AssetModalChrome>
  );
}
